import { Progress } from '@curology/ui-components-web/react/Progress';
import { RadioButton } from '@curology/ui-components-web/react/RadioButton';
import { RadioButtonGroup } from '@curology/ui-components-web/react/RadioButton/RadioButtonGroup';
import clsx from 'clsx';
import React, { useState } from 'react';

import RegisterApplePayPaymentMethodButton from '@/components/payments/components/registerApplePayPaymentMethodButton';
import RegisterCreditCardPaymentMethodForm from '@/components/payments/components/registerCreditCardPaymentMethodForm';
import RegisterPaypalPaymentMethodButton from '@/components/payments/components/registerPaypalPaymentMethodButton';
import { useBrowserPayment } from '@/components/payments/hooks/useBrowserPayment';
import { PAYMENT_METHODS } from '@/components/product-selection/consts/paymentMethods';
import { type CHECKOUT_CONTEXT } from '@/consts/checkout';
import { type NewPaymentMethod } from '@/services/resubscriptionService/types';
import { type PaymentInvoice } from '@/types/payments/types';

/**
 * Form to select a payment method and store in the backend.
 *
 * @prop paymentInvoice The payment invoice object for browser payments.
 * @prop onSuccess Executed on the successfully registered payment method.
 * @prop onError Executed on any error message in the workflow.
 * @prop checkoutContext Optional. Indicates context if rendering in a checkout flow.
 */
const SelectAndRegisterPaymentMethodForm: React.FC<{
  paymentInvoice: PaymentInvoice;
  onSuccess: (paymentMethod: NewPaymentMethod) => void;
  onError: (message: string) => void;
  checkoutContext?: valueof<typeof CHECKOUT_CONTEXT>;
  initialSelection?: valueof<typeof PAYMENT_METHODS>;
}> = ({
  paymentInvoice,
  onSuccess,
  onError,
  checkoutContext,
  initialSelection,
}) => {
  const BrowserPayment = useBrowserPayment(paymentInvoice);
  const [selectedMethod, setSelectedMethod] = useState<
    valueof<typeof PAYMENT_METHODS> | undefined
  >(initialSelection);

  const radioButtonClass = clsx(
    '[&>*:first-child]:cur-p-3 [&>*:first-child]:cur-ring-4',
    '[&>*:first-child]:cur-ring-surface-disabled',
    '[&[aria-selected="true"]>*:first-child]:cur-ring-border-highlight',
    '[&[aria-selected="true"]>*:first-child]:cur-bg-surface-highlight',
  );

  if (BrowserPayment.isLoading) {
    return (
      <Progress
        className="cur-inline-block"
        progressType="circle"
        state="highlight"
        size="lg"
      />
    );
  }

  return (
    <RadioButtonGroup
      name="payment method options"
      value={selectedMethod}
      onChange={(evt, value) =>
        setSelectedMethod(value as valueof<typeof PAYMENT_METHODS>)
      }
    >
      <RadioButton
        value={PAYMENT_METHODS.cc}
        label="Credit/debit card"
        className={radioButtonClass}
      >
        <div className="-cur-ml-8 cur-pt-4 cur-pb-2 data-hj-suppress">
          <RegisterCreditCardPaymentMethodForm
            onSuccess={onSuccess}
            onError={onError}
            checkoutContext={checkoutContext}
          />
        </div>
      </RadioButton>
      {BrowserPayment.canMakeApplePayPayments && (
        <RadioButton
          value={PAYMENT_METHODS.applePay}
          label="Apple Pay"
          className={radioButtonClass}
        >
          <div className="-cur-ml-8">
            <RegisterApplePayPaymentMethodButton
              paymentRequest={BrowserPayment.request}
              onSuccess={onSuccess}
              onError={onError}
              checkoutContext={checkoutContext}
            />
          </div>
        </RadioButton>
      )}
      <RadioButton
        value={PAYMENT_METHODS.payPal}
        label="PayPal"
        className={radioButtonClass}
      >
        <div className="-cur-ml-8">
          <RegisterPaypalPaymentMethodButton
            onSuccess={onSuccess}
            onError={onError}
            checkoutContext={checkoutContext}
          />
        </div>
      </RadioButton>
    </RadioButtonGroup>
  );
};

export default SelectAndRegisterPaymentMethodForm;
